import { QueryConstraint, where } from "firebase/firestore";
import { useContext, useEffect, useMemo, useState } from "react";
import { DefaultContext } from "../../contexts/default";
import Sale from "../../database/entities/sales/sale.entity";
import ComplementDB from "../../database/wrappers/complement";
import ProductDB from "../../database/wrappers/product";
import SalesDB from "../../database/wrappers/sales";
import DataFilter from "../../interface/resultContext/DataFilter";

const PRODUCT_TYPE = "Produto";
const COMPLEMENT_TYPE = "Complemento";

function getSaleDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getSaleOperator(sale: Sale, usersDicionary: any): string {
  if (!sale.userId) return "Não identificado";
  return usersDicionary?.[sale.userId]?.displayName || "Não identificado";
}

function hasValidName(name: any): boolean {
  return typeof name === "string" && name.trim().length > 0 && name.trim().toUpperCase() !== "N/I";
}

function getItemName(item: any, dicionary: Record<string, any>, fallback: string): string {
  if (hasValidName(item.name)) return item.name.trim();
  if (hasValidName(dicionary[item.id]?.name)) return dicionary[item.id].name.trim();
  return fallback;
}

function getWhere(dataFilter: DataFilter): QueryConstraint[] {
  const { store, firstDate, secondDate } = dataFilter;

  return [
    where("created_at", ">", new Date(firstDate)),
    where("created_at", "<", new Date(secondDate)),
    store !== "#" && where("storeId", "==", store),
  ].filter(Boolean) as QueryConstraint[];
}

function useSalesConsumptions(hidden: boolean, dataFilter: DataFilter) {
  const { usersDicionary } = useContext(DefaultContext);
  const [sales, setSales] = useState<Sale[]>([]);
  const [productsDicionary, setProductsDicionary] = useState<Record<string, any>>({});
  const [complementsDicionary, setComplementsDicionary] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [lastFilter, setLastFilter] = useState("");

  useEffect(() => {
    const filterKey = JSON.stringify(dataFilter || {});
    if (hidden || !dataFilter?.estab || lastFilter === filterKey) return;

    setLastFilter(filterKey);
    setLoading(true);

    Promise.all([
      new SalesDB(dataFilter.estab).getAll(...getWhere(dataFilter)),
      new ProductDB(dataFilter.estab).getAll(),
      new ComplementDB(dataFilter.estab).getAll(),
    ])
      .then(([salesData, products, complements]) => {
        setSales(salesData);
        setProductsDicionary(products.reduce((obj: Record<string, any>, product: any) => {
          obj[product.id] = product;
          return obj;
        }, {}));
        setComplementsDicionary(complements.reduce((obj: Record<string, any>, complement: any) => {
          obj[complement.id] = complement;
          return obj;
        }, {}));
      })
      .catch((error: any) => console.error(error))
      .finally(() => setLoading(false));
  }, [hidden, dataFilter, lastFilter]);

  const allConsumptions = useMemo(() => {
    const rows: any[] = [];

    sales.forEach((sale) => {
      if (sale.reversed) return;

      const created_at = getSaleDate(sale.created_at) || sale.created_at;
      const operator = getSaleOperator(sale, usersDicionary);

      (sale.products || []).forEach((product, productIndex) => {
        rows.push({
          id: `${sale.id}-product-${productIndex}`,
          created_at,
          name: getItemName(product, productsDicionary, "Produto sem nome"),
          operator,
          price: product.price,
          qtd: product.qtd,
          type: PRODUCT_TYPE,
          typeSale: 0,
        });

        (product.complements || []).forEach((complement, complementIndex) => {
          rows.push({
            id: `${sale.id}-product-${productIndex}-complement-${complementIndex}`,
            created_at,
            name: getItemName(complement, complementsDicionary, "Complemento sem nome"),
            operator,
            price: complement.price,
            qtd: complement.qtd,
            type: COMPLEMENT_TYPE,
            typeSale: 0,
          });
        });
      });
    });

    return rows;
  }, [sales, usersDicionary, productsDicionary, complementsDicionary]);

  return { allConsumptions, loading };
}

export default useSalesConsumptions;
